import MapView from "@arcgis/core/views/MapView";
import Basemap from "@arcgis/core/Basemap";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import { LayerType } from "../../types/layer-type";
import {
  focusOnFeature,
  getLayers,
  highlightSelectedObject,
  initializeMap,
  polylineOff,
  polylineOn,
  takeScreenshot,
  zoomIn,
  zoomOut,
} from "../actions/map";
import {
  MapSection,
  multiplePlotTypes,
  saveScreenshot,
  ScreenshotParams,
  setLayersVisible,
  setLoading,
  setMapComponentReady,
  validArcgisLayerTypes,
} from "../reducers/map";
import createViewer, {
  applyHoverEffect,
  changeBaseMap,
  CreateHybridBaseMap,
  createScreenshot,
  finishDrawingProcess,
  handleFocusOnFeature,
  highlightObject,
  startDrawingProcess,
  stopMeasuring,
  unFocus,
  waitForViewerReady,
} from "../../utils/helpers-for-2d-map";
import {
  LayersDataTypes,
  MainDataTypeIds,
} from "../../types/layers-data-types";
import { Middleware } from "redux";
import { RootState } from "../store";
import { ArcgisHybridLayer, Layer } from "../../interfaces/layer";
import UniqueValueRenderer from "@arcgis/core/renderers/UniqueValueRenderer";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import {
  deletePlotToMerge,
  setAdjacentPlot,
  setGeneralGeometry,
  setInitialPlot,
  setPlotToMerge,
} from "../reducers/multiple-plot";
import {
  applyPlotColor,
  resetPlotStyles,
  updateMergedGraphics,
} from "../../utils/helpers-for-multiple-plot-list";
import Polygon from "@arcgis/core/geometry/Polygon";
import TextSymbol from "@arcgis/core/symbols/TextSymbol";
import Graphic from "@arcgis/core/Graphic";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import { getFid } from "../../utils/plot-utils";

export const multiplePlotMiddleware = (
  section: MapSection,
): Middleware<{}, RootState> => {
  return (store) => {
    let viewer: MapView | null = null;
    let baseMap: Basemap;
    let textGraphicLayer: GraphicsLayer;
    let tempLineLayer: GraphicsLayer;
    const { dispatch } = store;
    function AddLayers(viewer: MapView) {
      const { layers, visibleLayersId } = store.getState().map;

      const baseMapLayer = layers.find(
        (l) =>
          l.dataType === LayersDataTypes.BaseMap &&
          visibleLayersId.includes(l.id),
      );
      if (baseMapLayer && baseMapLayer !== ArcgisHybridLayer) {
        changeBaseMap(baseMapLayer, viewer, getBaseMap);
      }
      const uniqueValueRenderer = new UniqueValueRenderer({
        valueExpression: `When($feature.is_inv_lan == 0, 'Filled','Default')`,
        uniqueValueInfos: [
          {
            value: "Filled",
            symbol: new SimpleFillSymbol({
              color: [176, 74, 90, 0.65],
              outline: { width: 1, color: [46, 26, 71] },
            }),
          },
        ],
        defaultSymbol: new SimpleFillSymbol({
          color: [46, 26, 71, 0.3],
          outline: {
            color: [46, 26, 71, 0.8],
            width: 1,
          },
        }),
      });

      layers
        .filter(
          (layer) =>
            multiplePlotTypes.includes(layer.dataType) &&
            layer.type === LayerType.ArcgisFeatureServer,
        )
        .forEach((layer) => {
          const featureLayer = new FeatureLayer({
            url: layer.url,
            id: layer.name,
            title: layer.label,
            visible: true,
            renderer: uniqueValueRenderer,
            outFields: ["*"],
            definitionExpression: layer.style2D?.query,
          });

          viewer.map.add(featureLayer);

          let hoverStyle = layer.style2D?.hover;
          applyHoverEffect(viewer, featureLayer, "style", hoverStyle);
        });
    }
    function getBaseMap(layer: Layer) {
      return layer === ArcgisHybridLayer ? baseMap : null;
    }
    function handleClickForMupltiplePlotList(
      viewer: MapView,
      event: __esri.ViewClickEvent,
    ) {
      viewer.hitTest(event).then((response) => {
        if (response.results.length === 0) return;
        const graphics = response.results
          .filter((res: __esri.ViewHit) => (res as __esri.GraphicHit).graphic)
          .map((res) => (res as __esri.GraphicHit).graphic);
        if (graphics.length === 0) return;
        const { layers } = store.getState().map;

        const { adjacentPlots, plotsToMerge, filledPlots } =
          store.getState().multiplePlotList;
        const filteredLayers = layers.filter((l) =>
          MainDataTypeIds.includes(l.dataType),
        );
        const result = graphics
          .map((graphic) => ({
            graphic: graphic,
            layer: filteredLayers.find((l) => l.name === graphic.layer.id),
          }))
          .find((r) => r.layer);
        if (!result) return;
        const { graphic } = result;
        const clickedPlotId = getFid(graphic.attributes).toString();
        const foundAdjacentPlot = adjacentPlots.find(
          (plot) => plot.fid === clickedPlotId,
        );
        if (foundAdjacentPlot) {
          dispatch(setPlotToMerge(foundAdjacentPlot));
          return;
        }

        const foundMergedPlot = plotsToMerge.find(
          (plot) => plot.fid === clickedPlotId,
        );
        if (foundMergedPlot) {
          dispatch(deletePlotToMerge(foundMergedPlot));
          return;
        }

        const foundFilledPlot = filledPlots.find(
          (plot) => plot.fid === clickedPlotId,
        );
        if (foundFilledPlot) {
          return;
        }
        return;
      });
    }

    async function InitialPlot(viewer: MapView) {
      const { initialPlot, adjacentPlots, plotsToMerge, filledPlots } =
        store.getState().multiplePlotList;
      if (!initialPlot) {
        return;
      }

      const mapLayer = viewer.map.layers.find(
        (l) => l.id === initialPlot?.layer?.name,
      ) as FeatureLayer;

      const objectIdGroups = {
        initial: [initialPlot.objectId],
        adjacent: adjacentPlots.map((p) => p.objectId),
        toMerge: plotsToMerge.map((p) => p.objectId),
        filled: filledPlots.map((p) => p.objectId),
      };

      const allObjectIds = [
        ...objectIdGroups.initial,
        ...objectIdGroups.adjacent,
        ...objectIdGroups.toMerge,
        ...objectIdGroups.filled,
      ];
      if (mapLayer)
        viewer
          .whenLayerView(mapLayer)
          .then(() => {
            const query = mapLayer.createQuery();
            query.where = `${mapLayer.objectIdField} IN (${allObjectIds.join(",")})`;
            query.outFields = ["*"];
            query.returnGeometry = true;
            return mapLayer.queryFeatures(query);
          })
          .then((result) => {
            const objectIdFieldName = mapLayer.objectIdField;

            if (!objectIdFieldName) {
              return;
            }

            const groupedFeatures = {
              initial: result.features.filter((f) =>
                objectIdGroups.initial.includes(
                  f.attributes[objectIdFieldName],
                ),
              ),
              adjacent: result.features.filter((f) =>
                objectIdGroups.adjacent.includes(
                  f.attributes[objectIdFieldName],
                ),
              ),
              toMerge: result.features.filter((f) =>
                objectIdGroups.toMerge.includes(
                  f.attributes[objectIdFieldName],
                ),
              ),
              filled: result.features.filter((f) =>
                objectIdGroups.filled.includes(f.attributes[objectIdFieldName]),
              ),
            };
            resetPlotStyles(allObjectIds, mapLayer);
            applyPlotColor(
              mapLayer,
              groupedFeatures,
              objectIdFieldName,
              viewer,
            );

            textGraphicLayer.removeAll();
            tempLineLayer.removeAll();

            Object.entries(groupedFeatures).forEach(([key, features]) => {
              if (key === "filled") return;

              features.forEach((feature) => {
                let centroid;

                if (feature.geometry instanceof Polygon) {
                  centroid = feature.geometry.centroid;
                }
                const textSymbol = new TextSymbol({
                  text: String(feature.attributes.PlotNum),
                  color: "#362044",
                  font: { size: 18, family: "sans-serif", weight: "normal" },
                });

                const textGraphic = new Graphic({
                  geometry: centroid,
                  symbol: textSymbol,
                });

                textGraphicLayer.add(textGraphic);
                viewer.map.reorder(
                  textGraphicLayer,
                  viewer.map.layers.length - 1,
                );
              });
            });
            const mergedGeometryObjects = [
              ...groupedFeatures.initial,
              ...groupedFeatures.toMerge,
              ...groupedFeatures.adjacent,
            ];

            const generalGeometryObjects = geometryEngine.union(
              mergedGeometryObjects.map((f) => f.geometry),
            );

            dispatch(setGeneralGeometry(generalGeometryObjects));

            updateMergedGraphics(
              groupedFeatures,
              tempLineLayer,
              viewer.map,
              dispatch,
            );
          });
    }
    return (next) => async (action) => {
      if (initializeMap.match(action)) {
        if (action.payload.section === section) {
          if (viewer === null) {
            await next(action);
            baseMap = CreateHybridBaseMap();
            viewer = createViewer(
              store,
              AddLayers,
              `${section}MapContainer`,
              baseMap,
            );

            textGraphicLayer = new GraphicsLayer();
            tempLineLayer = new GraphicsLayer();
            viewer.map.add(textGraphicLayer);
            viewer.map.add(tempLineLayer);
            viewer.on("click", function (event) {
              handleClickForMupltiplePlotList(viewer!, event);
            });
            await waitForViewerReady(viewer);
            dispatch(setMapComponentReady(section));
          }
          return;
        } else if (viewer !== null) {
          unFocus();
          viewer.map?.removeAll();
          viewer?.destroy();
          viewer = null;
          dispatch(setMapComponentReady(MapSection.none));
        }
      }
      if (viewer === null || store.getState().map.section !== section) {
        return next(action);
      }
      if (takeScreenshot.match(action)) {
        const payload = action.payload as ScreenshotParams;
        const { dataUrl } = await createScreenshot(
          viewer,
          { ...payload, graphicsLayers: [textGraphicLayer, tempLineLayer] },
          getBaseMap,
          false,
          true,
        );
        dispatch(
          saveScreenshot({
            name: payload.name,
            dataUrl,
          }),
        );
      }

      if (focusOnFeature.match(action)) {
        let {
          layer,
          geometry,
          objectId,
          addMarker,
          changeStyle,
          zoomAdjustment,
        } = action.payload;
        handleFocusOnFeature(
          layer,
          geometry,
          objectId,
          dispatch,
          store,
          viewer,
          addMarker,
          changeStyle,
          zoomAdjustment ?? null,
        );
      }

      if (
        setInitialPlot.match(action) ||
        setAdjacentPlot.match(action) ||
        setPlotToMerge.match(action)
      ) {
        next(action);
        InitialPlot(viewer);
        return;
      }
      if (setLayersVisible.match(action)) {
        next(action);
        const { layers, visibleLayersId } = store.getState().map;

        const plotsLayer = layers.find(
          (layer) => layer.dataType === LayersDataTypes.Plots,
        );
        const isPlotsLayerVisible =
          plotsLayer && visibleLayersId.includes(plotsLayer.id);

        textGraphicLayer.visible = !!isPlotsLayerVisible;
        tempLineLayer.visible = !!isPlotsLayerVisible;

        layers
          .filter((layer) => multiplePlotTypes.includes(layer.dataType))
          .forEach((layer) => {
            if (layer.dataType === LayersDataTypes.BaseMap) {
              if (visibleLayersId.includes(layer.id))
                changeBaseMap(layer, viewer, getBaseMap);
            } else if (validArcgisLayerTypes.includes(layer.type)) {
              let mapLayers = viewer!.map.layers.filter(
                (l) => l.id === layer.name,
              );
              if (mapLayers) {
                const visible =
                  visibleLayersId.includes(layer.id) ||
                  (!!layer.parentId &&
                    visibleLayersId.includes(layer.parentId));
                Promise.all(
                  mapLayers.map((mapLayer) => {
                    mapLayer.visible = visible;
                    return mapLayer.load();
                  }),
                ).finally(() => {
                  dispatch(setLoading({ id: layer.id, loading: false }));
                });
              }
            }
          });

        return;
      }
      if (zoomIn.match(action)) {
        viewer.zoom += 1;
      }
      if (zoomOut.match(action)) {
        viewer.zoom -= 1;
      }

      if (getLayers.fulfilled.match(action)) {
        next(action);
        AddLayers(viewer);
        return;
      }

      if (highlightSelectedObject.match(action)) {
        let { objectId, layer } = action.payload;
        highlightObject(objectId, layer, viewer);
      }

      if (polylineOff.match(action)) {
        finishDrawingProcess(viewer);
        stopMeasuring(viewer);
      }
      if (polylineOn.match(action)) {
        startDrawingProcess(viewer);
      }

      next(action);
    };
  };
};
